import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../hooks/useTranslation";
import { fetchPatientProfileByIdThunk, updatePatientProfileThunk } from "../../store/slices/patientProfilesReducer";
import { fetchMealsThunk, updateMealThunk } from "../../store/slices/mealsReducer";
import { createPatientMealInstanceThunk, fetchPatientMealInstancesThunk, deletePatientMealInstanceThunk } from "../../store/slices/patientMealInstancesReducer";
import { fetchMeals } from "../../api/api_meals";
import JourneyBreadcrumb from "../../components/navigation/JourneyBreadcrumb";
import { getDishAverages } from "../../utils/sustainabilityAverages";
import { useAppAlertDialog } from "../../hooks/useAppAlertDialog";

const PatientProfilePage = () => {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { currentProfile, loading, error } = useSelector((state) => state.patientProfiles);
  const { list: meals } = useSelector((state) => state.meals);
  const patientMealInstances = useSelector((state) => state.patientMealInstances?.instances || []);
  
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [standaloneMeals, setStandaloneMeals] = useState([]);
  const [loadingStandaloneMeals, setLoadingStandaloneMeals] = useState(false);
  const [assigningMealId, setAssigningMealId] = useState(null);
  const [selectedMeals, setSelectedMeals] = useState(new Set());
  const [modalSearchQuery, setModalSearchQuery] = useState("");
  const [editingMealId, setEditingMealId] = useState(null);
  const [editingMealName, setEditingMealName] = useState("");
  const [savingMealName, setSavingMealName] = useState(false);
  const { showAlert, showConfirm, dialogNode } = useAppAlertDialog({
    cancelLabel: t("Avbryt"),
    confirmLabel: t("Fortsätt"),
    closeLabel: t("Stäng"),
  });

  useEffect(() => {
    if (patientId) {
      dispatch(fetchPatientProfileByIdThunk(patientId));
      // Fetch patient meal instances to see which meals are assigned via instances
      dispatch(fetchPatientMealInstancesThunk({ patient_profile_id: patientId }));
      // Also fetch meals with patient_profile_id for backward compatibility
      dispatch(fetchMealsThunk({ patient_profile_id: patientId }));
    }
  }, [dispatch, patientId]);

  // Load selected meals from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(`selectedMeals_${patientId}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSelectedMeals(new Set(parsed));
      } catch (e) {
        console.error('Failed to parse selected meals from localStorage', e);
      }
    }
  }, [patientId]);

  // Save selected meals to localStorage when they change
  useEffect(() => {
    if (selectedMeals.size > 0) {
      localStorage.setItem(`selectedMeals_${patientId}`, JSON.stringify(Array.from(selectedMeals)));
    } else {
      localStorage.removeItem(`selectedMeals_${patientId}`);
    }
  }, [selectedMeals, patientId]);

  // Fetch assignable meals when modal opens - Use direct API call to avoid polluting Redux
  useEffect(() => {
    if (showAssignModal && currentProfile?.status === 'active') {
      setModalSearchQuery(""); // Reset search when modal opens
      setLoadingStandaloneMeals(true);
      // Fetch ALL meals (standalone and assigned to other patients)
      // We'll filter out meals already assigned to current patient
      fetchMeals({})
        .then((response) => {
          if (response?.data?.data) {
            const currentPatientId = parseInt(patientId);
            // Filter out meals that are already assigned to the CURRENT patient
            // Check both patient_profile_id AND patient_meal_instances
            const mealIdsAlreadyAssigned = new Set(
              (patientMealInstances || [])
                .filter(instance => instance && parseInt(instance.patient_profile_id) === currentPatientId)
                .map(instance => instance.meal_id)
            );
            
            const assignableMeals = response.data.data.filter(meal => {
              // Exclude if meal has patient_profile_id matching current patient (old way)
              if (meal.patient_profile_id && parseInt(meal.patient_profile_id) === currentPatientId) {
                return false;
              }
              // Exclude if meal is already assigned via patient_meal_instances
              if (mealIdsAlreadyAssigned.has(meal.id)) {
                return false;
              }
              // All other meals are assignable (standalone or assigned to other patients)
              return true;
            });
            setStandaloneMeals(assignableMeals);
          } else {
            setStandaloneMeals([]);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch assignable meals:', error);
          setStandaloneMeals([]);
        })
        .finally(() => {
          setLoadingStandaloneMeals(false);
        });
    } else if (!showAssignModal) {
      // Clear assignable meals when modal closes
      setStandaloneMeals([]);
      setModalSearchQuery("");
    }
  }, [showAssignModal, currentProfile?.status, patientId, patientMealInstances]);

  // Get meals assigned to current patient - combine meals with patient_profile_id and meals from patient_meal_instances
  const mealsFromPatientProfileId = meals.filter(meal => 
    meal.patient_profile_id && parseInt(meal.patient_profile_id) === parseInt(patientId)
  );
  
  // Get meals from patient_meal_instances
  const mealsFromInstances = (patientMealInstances || [])
    .filter(instance => instance && instance.patient_profile_id && parseInt(instance.patient_profile_id) === parseInt(patientId))
    .map(instance => {
      // Find the meal from meals list or use the meal from instance
      const meal = meals.find(m => m.id === instance.meal_id) || instance.meal;
      return meal ? { ...meal, instance_id: instance.id } : null;
    })
    .filter(Boolean); // Remove nulls
  
  // Combine both, but avoid duplicates (same meal_id)
  const patientMealsMap = new Map();
  mealsFromPatientProfileId.forEach(meal => {
    patientMealsMap.set(meal.id, meal);
  });
  mealsFromInstances.forEach(meal => {
    if (!patientMealsMap.has(meal.id)) {
      patientMealsMap.set(meal.id, meal);
    }
  });
  const patientMeals = Array.from(patientMealsMap.values());

  // Group meals by meal_type
  const mealsByType = patientMeals.reduce((acc, meal) => {
    const type = meal.meal_type || 'övrigt';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(meal);
    return acc;
  }, {});

  const mealTypeLabels = {
    frukost: t("Frukost") || "Frukost",
    lunch: t("Lunch") || "Lunch",
    middag: t("Middag") || "Middag",
    mellanmål: t("Mellanmål") || "Mellanmål",
    småmål: t("Småmål") || "Småmål",
    kvällmål: t("Kvällsmål") || "Kvällsmål",
    dessert: t("Desser") || "Desser",
    övrigt: t("Övrigt") || "Övrigt",
  };

  // Meal type colors - only for badges and borders
  const mealTypeColors = {
    frukost: { border: 'border-yellow-300', text: 'text-yellow-800', badge: 'bg-yellow-100' },
    lunch: { border: 'border-blue-300', text: 'text-blue-800', badge: 'bg-blue-100' },
    middag: { border: 'border-purple-300', text: 'text-purple-800', badge: 'bg-purple-100' },
    mellanmål: { border: 'border-green-300', text: 'text-green-800', badge: 'bg-green-100' },
    småmål: { border: 'border-orange-300', text: 'text-orange-800', badge: 'bg-orange-100' },
    kvällmål: { border: 'border-indigo-300', text: 'text-indigo-800', badge: 'bg-indigo-100' },
    dessert: { border: 'border-pink-300', text: 'text-pink-800', badge: 'bg-pink-100' },
    övrigt: { border: 'border-gray-300', text: 'text-gray-800', badge: 'bg-gray-100' },
  };

  // Handle meal selection
  const toggleMealSelection = (mealId) => {
    setSelectedMeals(prev => {
      const next = new Set(prev);
      if (next.has(mealId)) {
        next.delete(mealId);
      } else {
        next.add(mealId);
      }
      return next;
    });
  };

  // Select/deselect all meals for current patient
  const toggleSelectAll = () => {
    const allMealIds = patientMeals.map(m => m.id);
    if (selectedMeals.size === allMealIds.length && allMealIds.every(id => selectedMeals.has(id))) {
      setSelectedMeals(new Set());
    } else {
      setSelectedMeals(new Set(allMealIds));
    }
  };

  // Bulk remove assignment (unassign meals from patient)
  const handleBulkRemoveAssignment = async () => {
    if (selectedMeals.size === 0) return;
    
    const selectedCount = selectedMeals.size;
    const selectedMealIds = Array.from(selectedMeals);
    const mealNames = patientMeals
      .filter(m => selectedMeals.has(m.id))
      .map(m => m.name)
      .join(', ');
    
    const confirmed = await showConfirm(
      t("Ta bort tilldelning"),
      t("Är du säker på att du vill ta bort tilldelningen för {{count}} måltider?", { count: selectedCount }) ||
        `Är du säker på att du vill ta bort tilldelningen för ${selectedCount} måltider?\n\n${mealNames}`
    );
    if (!confirmed) {
      return;
    }

    try {
      // Handle both old way (patient_profile_id) and new way (patient_meal_instances)
      const unassignPromises = [];
      
      for (const mealId of selectedMealIds) {
        const meal = patientMeals.find(m => m.id === mealId);
        
        // If meal was assigned via patient_meal_instances, delete those instances
        const instancesToDelete = (patientMealInstances || [])
          .filter(instance => 
            instance && 
            parseInt(instance.patient_profile_id) === parseInt(patientId) &&
            instance.meal_id === mealId
          );
        
        if (instancesToDelete.length > 0) {
          // Delete all instances for this meal and patient
          for (const instance of instancesToDelete) {
            unassignPromises.push(
              dispatch(deletePatientMealInstanceThunk(instance.id)).unwrap()
            );
          }
        }
        
        // Also handle old way - if meal has patient_profile_id matching current patient, set to null
        if (meal && meal.patient_profile_id && parseInt(meal.patient_profile_id) === parseInt(patientId)) {
          unassignPromises.push(
            dispatch(updateMealThunk({
              id: mealId,
              mealData: { patient_profile_id: null }
            })).unwrap()
          );
        }
      }
      
      await Promise.all(unassignPromises);
      
      // Refresh patient meals and instances
      await dispatch(fetchPatientMealInstancesThunk({ patient_profile_id: patientId }));
      await dispatch(fetchMealsThunk({ patient_profile_id: patientId }));
      
      // Clear selection
      setSelectedMeals(new Set());
      
    } catch (error) {
      console.error('Failed to remove assignments:', error);
      showAlert(
        t("Fel"),
        t("Kunde inte ta bort tilldelningar. Försök igen.") || "Kunde inte ta bort tilldelningar. Försök igen."
      );
    }
  };

  // Handle meal name edit
  const handleStartEditName = (mealId, currentName, e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingMealId(mealId);
    setEditingMealName(currentName || "");
  };

  const handleCancelEditName = () => {
    setEditingMealId(null);
    setEditingMealName("");
  };

  const handleSaveMealName = async (mealId) => {
    if (!editingMealName.trim() || savingMealName) return;
    
    const trimmedName = editingMealName.trim();
    const meal = patientMeals.find(m => m.id === mealId);
    if (meal && meal.name === trimmedName) {
      // No change, just cancel
      handleCancelEditName();
      return;
    }

    setSavingMealName(true);
    try {
      await dispatch(updateMealThunk({
        id: mealId,
        mealData: { name: trimmedName }
      })).unwrap();

      // Refresh patient meals to get updated data
      await dispatch(fetchMealsThunk({ patient_profile_id: patientId }));
      
      handleCancelEditName();
    } catch (error) {
      console.error('Failed to update meal name:', error);
      showAlert(
        t("Fel"),
        t("Kunde inte uppdatera måltidsnamnet. Försök igen.") || "Kunde inte uppdatera måltidsnamnet. Försök igen."
      );
    } finally {
      setSavingMealName(false);
    }
  };

  const handleKeyDown = (e, mealId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveMealName(mealId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEditName();
    }
  };

  // Compact Meal Card Component
  const CompactMealCard = ({ meal, showCheckbox = false, onCheckboxChange = null }) => {
    const mealType = meal.meal_type || 'övrigt';
    const colors = mealTypeColors[mealType] || mealTypeColors.övrigt;
    const averages = getDishAverages(meal);
    const overallScore = Math.round((averages.environment_average + averages.health_average + averages.social_average) / 3);
    const isSelected = selectedMeals.has(meal.id);
    const isEditing = editingMealId === meal.id;

    return (
      <div className={`group relative block bg-white rounded-lg border ${
        isSelected ? 'border-secondary-green ring-2 ring-secondary-green/20' : 'border-gray-200'
      } hover:shadow-lg hover:border-secondary-green hover:scale-[1.02] transition-all duration-200 overflow-hidden shadow-sm`}>
        {/* Checkbox */}
        {showCheckbox && onCheckboxChange && (
          <div 
            className="absolute top-2 left-2 z-10"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onCheckboxChange(meal.id);
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-5 h-5 text-secondary-green border-gray-300 rounded focus:ring-secondary-green cursor-pointer"
            />
          </div>
        )}
        
        {!isEditing ? (
          <Link
            to={`/kostdata/meals/${meal.id}/edit`}
            className="block"
          >
            {/* Meal Image or Placeholder - Smaller */}
            <div className="relative w-full h-20 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
              {meal.image ? (
                <img
                  src={meal.image}
                  alt={meal.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div className={`w-full h-full flex items-center justify-center ${meal.image ? 'hidden' : 'flex'}`}>
                <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              
              {/* Meal Type Badge - Smaller - Adjust position when checkbox is shown */}
              <div className={`absolute top-1 ${showCheckbox && onCheckboxChange ? 'left-8' : 'left-1'}`}>
                <span className={`px-1.5 py-0.5 ${colors.badge} ${colors.text} rounded text-[10px] font-semibold backdrop-blur-sm`}>
                  {mealTypeLabels[mealType]}
                </span>
              </div>

              {/* Overall Score Badge - Smaller */}
              <div className="absolute top-1 right-1">
                <div className="w-7 h-7 bg-white bg-opacity-95 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm">
                  <span className="text-[10px] font-bold text-gray-700">{overallScore}</span>
                </div>
              </div>
            </div>

            {/* Content - More compact */}
            <div className="p-2">
              <h3 
                className="font-semibold text-gray-900 mb-0.5 line-clamp-1 text-xs group-hover:text-secondary-green transition-colors cursor-pointer"
                onClick={(e) => handleStartEditName(meal.id, meal.name, e)}
                title={t("Klicka för att redigera") || "Klicka för att redigera"}
              >
                {meal.name}
              </h3>
              
              {/* Quick Stats - Compact single line */}
              <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1.5">
                <span className="flex items-center gap-0.5">
                  <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  {meal.components?.length || 0}
                </span>
                {meal.size && meal.unit_abbr && (
                  <span className="text-[10px]">
                    {meal.size} {meal.unit_abbr}
                  </span>
                )}
              </div>

              {/* Sustainability Scores - Just numbers, no circles */}
              <div className="flex items-center justify-between gap-1 pt-1.5 border-t border-gray-200">
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[11px] font-semibold text-gray-700">{Math.round(averages.environment_average || 0)}</span>
                  <span className="text-[9px] text-gray-500 leading-tight">M</span>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[11px] font-semibold text-gray-700">{Math.round(averages.health_average || 0)}</span>
                  <span className="text-[9px] text-gray-500 leading-tight">H</span>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[11px] font-semibold text-gray-700">{Math.round(averages.social_average || 0)}</span>
                  <span className="text-[9px] text-gray-500 leading-tight">S</span>
                </div>
              </div>
            </div>
          </Link>
        ) : (
          <>
            {/* Meal Image or Placeholder - Smaller */}
            <div className="relative w-full h-20 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
              {meal.image ? (
                <img
                  src={meal.image}
                  alt={meal.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div className={`w-full h-full flex items-center justify-center ${meal.image ? 'hidden' : 'flex'}`}>
                <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              
              {/* Meal Type Badge - Smaller - Adjust position when checkbox is shown */}
              <div className={`absolute top-1 ${showCheckbox && onCheckboxChange ? 'left-8' : 'left-1'}`}>
                <span className={`px-1.5 py-0.5 ${colors.badge} ${colors.text} rounded text-[10px] font-semibold backdrop-blur-sm`}>
                  {mealTypeLabels[mealType]}
                </span>
              </div>

              {/* Overall Score Badge - Smaller */}
              <div className="absolute top-1 right-1">
                <div className="w-7 h-7 bg-white bg-opacity-95 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm">
                  <span className="text-[10px] font-bold text-gray-700">{overallScore}</span>
                </div>
              </div>
            </div>

            {/* Content - More compact */}
            <div className="p-2">
              <div className="mb-0.5" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={editingMealName}
                  onChange={(e) => setEditingMealName(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, meal.id)}
                  onBlur={() => handleSaveMealName(meal.id)}
                  autoFocus
                  disabled={savingMealName}
                  className="w-full px-1.5 py-0.5 text-xs font-semibold text-gray-900 border border-secondary-green rounded focus:outline-none focus:ring-1 focus:ring-secondary-green"
                  placeholder={t("Måltidsnamn") || "Måltidsnamn"}
                />
              </div>
              
              {/* Quick Stats - Compact single line */}
              <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1.5">
                <span className="flex items-center gap-0.5">
                  <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  {meal.components?.length || 0}
                </span>
                {meal.size && meal.unit_abbr && (
                  <span className="text-[10px]">
                    {meal.size} {meal.unit_abbr}
                  </span>
                )}
              </div>

              {/* Sustainability Scores - Just numbers, no circles */}
              <div className="flex items-center justify-between gap-1 pt-1.5 border-t border-gray-200">
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[11px] font-semibold text-gray-700">{Math.round(averages.environment_average || 0)}</span>
                  <span className="text-[9px] text-gray-500 leading-tight">M</span>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[11px] font-semibold text-gray-700">{Math.round(averages.health_average || 0)}</span>
                  <span className="text-[9px] text-gray-500 leading-tight">H</span>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[11px] font-semibold text-gray-700">{Math.round(averages.social_average || 0)}</span>
                  <span className="text-[9px] text-gray-500 leading-tight">S</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Handle meal assignment
  const handleAssignMeal = async (mealId) => {
    if (currentProfile.status !== 'active') {
      showAlert(
        t("Kan inte tilldela"),
        t("Aktivera patienten först innan du kan tilldela måltider") || "Aktivera patienten först innan du kan tilldela måltider"
      );
      return;
    }

    const meal = standaloneMeals.find(m => m.id === mealId);
    if (!meal) {
      showAlert(t("Fel"), t("Måltiden kunde inte hittas") || "Måltiden kunde inte hittas");
      return;
    }
    
    const currentPatientId = parseInt(patientId);
    
    // Check if meal is already assigned to current patient (should not happen due to filtering, but defensive check)
    if (meal.patient_profile_id && parseInt(meal.patient_profile_id) === currentPatientId) {
      showAlert(
        t("Redan tilldelad"),
        t("Denna måltid är redan tilldelad till denna patient") || "Denna måltid är redan tilldelad till denna patient"
      );
      // Remove from list and refresh
      setStandaloneMeals(prev => prev.filter(m => m.id !== mealId));
      return;
    }

    // Check if current patient already has this meal by checking the patient's meals list
    const patientAlreadyHasMeal = patientMeals.some(m => m.id === mealId);
    if (patientAlreadyHasMeal) {
      showAlert(
        t("Redan tilldelad"),
        t("Denna patient har redan denna måltid tilldelad") || "Denna patient har redan denna måltid tilldelad"
      );
      // Remove from list and refresh
      setStandaloneMeals(prev => prev.filter(m => m.id !== mealId));
      return;
    }

    // Check if patient already has this meal via patient_meal_instances
    const patientAlreadyHasMealInstance = (patientMealInstances || []).some(instance => 
      instance && instance.meal_id === mealId && instance.patient_profile_id === currentPatientId
    );
    if (patientAlreadyHasMealInstance) {
      showAlert(
        t("Redan tilldelad"),
        t("Denna patient har redan denna måltid tilldelad") || "Denna patient har redan denna måltid tilldelad"
      );
      setStandaloneMeals(prev => prev.filter(m => m.id !== mealId));
      return;
    }

    const confirmMessage = t("Vill du tilldela denna måltid till {{patientName}}?", { patientName: currentProfile.name }) || `Vill du tilldela denna måltid till ${currentProfile.name}?`;
    const confirmedAssign = await showConfirm(t("Tilldela måltid"), confirmMessage);
    if (!confirmedAssign) {
      return;
    }

    setAssigningMealId(mealId);
    try {
      // Create patient_meal_instance instead of changing patient_profile_id
      // This allows the same meal to be assigned to multiple patients
      const today = new Date();
      const currentWeekStart = new Date(today);
      currentWeekStart.setDate(today.getDate() - today.getDay()); // Start of current week (Sunday)

      await dispatch(createPatientMealInstanceThunk({
        patient_profile_id: currentPatientId,
        meal_id: mealId,
        meal_type: meal.meal_type || 'övrigt',
        day_of_week: today.getDay(), // 0 = Sunday, 1 = Monday, etc.
        week_start_date: currentWeekStart.toISOString().split('T')[0], // YYYY-MM-DD format
        notes: null
      })).unwrap();

      // Refresh patient meal instances (not meals with patient_profile_id)
      await dispatch(fetchPatientMealInstancesThunk({ patient_profile_id: patientId }));
      // Also refresh meals for backward compatibility
      await dispatch(fetchMealsThunk({ patient_profile_id: patientId }));
      
      // Remove assigned meal from list
      setStandaloneMeals(prev => prev.filter(m => m.id !== mealId));
      
      // Close modal if no more assignable meals
      if (filteredStandaloneMeals.length === 1) {
        setShowAssignModal(false);
        setModalSearchQuery("");
      }
    } catch (error) {
      console.error('Failed to assign meal:', error);
      showAlert(
        t("Fel"),
        error?.message || t("Kunde inte tilldela måltiden. Försök igen.") || "Kunde inte tilldela måltiden. Försök igen."
      );
    } finally {
      setAssigningMealId(null);
    }
  };

  // Filter standalone meals by search (use modalSearchQuery for modal, searchQuery for main page)
  const filteredStandaloneMeals = standaloneMeals.filter(meal => {
    if (!modalSearchQuery.trim()) return true;
    const query = modalSearchQuery.toLowerCase();
    return meal.name?.toLowerCase().includes(query) || 
           meal.description?.toLowerCase().includes(query);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-gray-200 rounded-full animate-pulse"></div>
          <p className="mt-8 text-gray-600 text-lg font-medium">{t("Laddar patientprofil...")}</p>
        </div>
      </div>
    );
  }

  if (error || !currentProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error || t("Patient hittades inte")}</p>
          <button
            onClick={() => navigate('/kostdata/patients')}
            className="mt-4 px-4 py-2 bg-secondary-green text-white rounded-lg hover:opacity-90"
          >
            {t("Tillbaka till patientlistan")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 tablet:px-6 laptop:px-8 desktop:px-8 py-8">
        <JourneyBreadcrumb
          items={[
            { label: t("Patientprofiler") || "Patientprofiler", to: "/kostdata/patients" },
            { label: currentProfile.name },
          ]}
          className="mb-6"
        />

        {/* Patient Header */}
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-secondary-green/20 to-secondary-green/30 rounded-full flex items-center justify-center">
                <span className="text-3xl font-bold text-secondary-green">
                  {currentProfile.name ? currentProfile.name.charAt(0).toUpperCase() : '?'}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{currentProfile.name}</h1>
                <div className="mt-2 space-y-1">
                  {currentProfile.age && (
                    <p className="text-sm text-gray-600">{t("Ålder")}: {currentProfile.age} {t("år")}</p>
                  )}
                  {currentProfile.address && (
                    <p className="text-sm text-gray-600">{currentProfile.address}</p>
                  )}
                  <button
                    onClick={async () => {
                      const newStatus = currentProfile.status === 'active' ? 'inactive' : 'active';
                      setUpdatingStatus(true);
                      try {
                        await dispatch(updatePatientProfileThunk({
                          id: currentProfile.id,
                          profileData: { status: newStatus }
                        })).unwrap();
                      } catch (error) {
                        showAlert(t("Fel"), t('Kunde inte uppdatera patientstatus. Försök igen.'));
                        console.error('Failed to update patient status:', error);
                      } finally {
                        setUpdatingStatus(false);
                      }
                    }}
                    disabled={updatingStatus}
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold transition-all hover:shadow-md ${
                      currentProfile.status === 'active' 
                        ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    } ${updatingStatus ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    title={currentProfile.status === 'active' ? t('Klicka för att markera som inaktiv') : t('Klicka för att markera som aktiv')}
                  >
                    {updatingStatus ? (
                      <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      <>
                        {currentProfile.status === 'active' ? t('Aktiva') : t('Inaktiva')}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {currentProfile.status === 'active' && (
                <button
                  onClick={() => setShowAssignModal(true)}
                  className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 border border-gray-300 hover:border-gray-400 transition-colors font-semibold"
                >
                  {t("Tilldela befintlig måltid") || "Tilldela befintlig måltid"}
                </button>
              )}
              <Link
                to={`/kostdata/meals/new?patientId=${patientId}`}
                className="px-4 py-2 bg-secondary-green text-white rounded-lg hover:opacity-90 transition-opacity font-semibold"
              >
                {t("Skapa ny måltid") || "Skapa ny måltid"}
              </Link>
            </div>
          </div>
          
          {currentProfile.notes && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">{t("Anteckningar")}</h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{currentProfile.notes}</p>
            </div>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {selectedMeals.size > 0 && (
          <div className="mb-6 flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <span className="text-sm font-medium text-gray-700">
              {selectedMeals.size} {t("måltider valda") || "måltider valda"}
            </span>
            <button
              onClick={toggleSelectAll}
              className="text-sm text-secondary-green hover:underline"
            >
              {selectedMeals.size === patientMeals.length && patientMeals.length > 0 ? (t("Avmarkera alla") || "Avmarkera alla") : (t("Markera alla") || "Markera alla")}
            </button>
            <div className="flex-1"></div>
            <button
              onClick={handleBulkRemoveAssignment}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
            >
              {t("Ta bort tilldelning") || "Ta bort tilldelning"}
            </button>
          </div>
        )}

        {/* Meals by Type */}
        <div className="space-y-6">
          {Object.keys(mealsByType).length > 0 ? (
            Object.entries(mealsByType).map(([type, typeMeals]) => (
              <div key={type}>
                <div className="grid grid-cols-1 mobile:grid-cols-2 tablet:grid-cols-3 laptop:grid-cols-4 desktop:grid-cols-5 gap-3">
                  {typeMeals.map((meal) => (
                    <CompactMealCard 
                      key={meal.id} 
                      meal={meal} 
                      showCheckbox={true}
                      onCheckboxChange={toggleMealSelection}
                    />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <p className="text-gray-600 text-lg mb-4">
                {t("Inga måltider ännu för denna patient.") || "Inga måltider ännu för denna patient."}
              </p>
              {currentProfile.status === 'active' ? (
                <Link
                  to={`/kostdata/meals/new?patientId=${patientId}`}
                  className="inline-block px-4 py-2 bg-secondary-green text-white rounded-lg hover:opacity-90 transition-opacity font-semibold"
                >
                  {t("Skapa första måltiden") || "Skapa första måltiden"}
                </Link>
              ) : (
                <div className="inline-block px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed font-semibold">
                  {t("Skapa första måltiden") || "Skapa första måltiden"}
                </div>
              )}
              {currentProfile.status === 'inactive' && (
                <p className="mt-2 text-sm text-orange-600">
                  {t("Aktivera patienten för att kunna skapa måltider") || "Aktivera patienten för att kunna skapa måltider"}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Assign Meal Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
            onClick={() => !assigningMealId && setShowAssignModal(false)} 
          />
          <div className="absolute inset-0 flex items-center justify-center p-4 overflow-y-auto">
            <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden my-8">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {t("Tilldela befintlig måltid") || "Tilldela befintlig måltid"}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {t("Välj en måltid att tilldela till") || "Välj en måltid att tilldela till"} <span className="font-semibold">{currentProfile.name}</span>
                  </p>
                </div>
                <button
                  className="inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-200 transition-colors"
                  onClick={() => !assigningMealId && setShowAssignModal(false)}
                  disabled={!!assigningMealId}
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Search */}
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t("Sök måltider...") || "Sök måltider..."}
                    value={modalSearchQuery}
                    onChange={(e) => setModalSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-green focus:border-transparent"
                  />
                  <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                {loadingStandaloneMeals ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="w-12 h-12 border-4 border-secondary-green border-t-transparent rounded-full animate-spin mx-auto"></div>
                      <p className="mt-4 text-gray-600">{t("Laddar måltider...") || "Laddar måltider..."}</p>
                    </div>
                  </div>
                ) : filteredStandaloneMeals.length === 0 ? (
                  <div className="text-center py-12">
                    <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    <p className="text-gray-600 text-lg font-medium">
                      {modalSearchQuery 
                        ? (t("Inga måltider matchade din sökning") || "Inga måltider matchade din sökning")
                        : (t("Inga fristående måltider tillgängliga") || "Inga fristående måltider tillgängliga")
                      }
                    </p>
                    {!modalSearchQuery && (
                      <p className="text-gray-500 text-sm mt-2">
                        {t("Skapa en ny måltid först för att kunna tilldela den till patienter") || "Skapa en ny måltid först för att kunna tilldela den till patienter"}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 mobile:grid-cols-2 tablet:grid-cols-3 laptop:grid-cols-4 gap-4">
                    {filteredStandaloneMeals.map((meal) => {
                      const mealType = meal.meal_type || 'övrigt';
                      const colors = mealTypeColors[mealType] || mealTypeColors.övrigt;
                      const averages = getDishAverages(meal);
                      const overallScore = Math.round((averages.environment_average + averages.health_average + averages.social_average) / 3);
                      const isAssigning = assigningMealId === meal.id;

                      return (
                        <div
                          key={meal.id}
                          className={`relative bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm transition-all duration-200 ${
                            isAssigning ? 'opacity-50 pointer-events-none' : 'hover:shadow-md hover:border-secondary-green'
                          }`}
                        >
                          {/* Meal Image */}
                          <div className="relative w-full h-20 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                            {meal.image ? (
                              <img
                                src={meal.image}
                                alt={meal.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null}
                            <div className={`w-full h-full flex items-center justify-center ${meal.image ? 'hidden' : 'flex'}`}>
                              <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                            </div>
                            
                            {/* Meal Type Badge */}
                            <div className="absolute top-1 left-1">
                              <span className={`px-1.5 py-0.5 ${colors.badge} ${colors.text} rounded text-[10px] font-semibold`}>
                                {mealTypeLabels[mealType]}
                              </span>
                            </div>

                            {/* Overall Score Badge */}
                            <div className="absolute top-1 right-1">
                              <div className="w-7 h-7 bg-white bg-opacity-95 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm">
                                <span className="text-[10px] font-bold text-gray-700">{overallScore}</span>
                              </div>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="p-2">
                            <h3 className="font-semibold text-gray-900 mb-0.5 line-clamp-1 text-xs">
                              {meal.name}
                            </h3>
                            
                            {/* Quick Stats */}
                            <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1.5">
                              <span>{meal.components?.length || 0} {t("komponenter") || "komponenter"}</span>
                              {meal.size && meal.unit_abbr && (
                                <span>{meal.size} {meal.unit_abbr}</span>
                              )}
                            </div>

                            {/* Sustainability Scores */}
                            <div className="flex items-center justify-between gap-1 pt-1.5 border-t border-gray-200 mb-2">
                              <div className="flex flex-col items-center flex-1">
                                <span className="text-[11px] font-semibold text-gray-700">{Math.round(averages.environment_average || 0)}</span>
                                <span className="text-[9px] text-gray-500 leading-tight">M</span>
                              </div>
                              <div className="flex flex-col items-center flex-1">
                                <span className="text-[11px] font-semibold text-gray-700">{Math.round(averages.health_average || 0)}</span>
                                <span className="text-[9px] text-gray-500 leading-tight">H</span>
                              </div>
                              <div className="flex flex-col items-center flex-1">
                                <span className="text-[11px] font-semibold text-gray-700">{Math.round(averages.social_average || 0)}</span>
                                <span className="text-[9px] text-gray-500 leading-tight">S</span>
                              </div>
                            </div>

                            {/* Assign Button - Only assigns when explicitly clicked */}
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleAssignMeal(meal.id);
                              }}
                              disabled={isAssigning || currentProfile.status !== 'active'}
                              className={`w-full px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                isAssigning
                                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                  : currentProfile.status === 'active'
                                  ? 'bg-secondary-green text-white hover:bg-secondary-green/90'
                                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              {isAssigning ? (
                                <span className="flex items-center justify-center gap-1">
                                  <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                                  {t("Tilldelar...") || "Tilldelar..."}
                                </span>
                              ) : (
                                t("Tilldela") || "Tilldela"
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end">
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setModalSearchQuery("");
                  }}
                  disabled={!!assigningMealId}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("Stäng") || "Stäng"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {dialogNode}
    </div>
  );
};

export default PatientProfilePage;

